import { useFormikContext } from 'formik'
import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import {
  AgentInformation,
  AgentInformationType,
  useAgentInformationFetch,
} from '@pages/Traders-Room/WalletAgentInformation'
import { useFundingCurrencySelector } from '@pages/Traders-Room/shared/hooks/useFundingCurrencySelector'

import { useProductReadContext } from '@context/ProductContext'

import { Caption1, Heading6 } from '@ui/Typography'
import { Button, IconButton } from '@ui/components/Button'
import { MinusIcon } from '@ui/icons/Minus.icon'
import { PlusIcon } from '@ui/icons/Plus.icon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { useWindowResize } from '@utils/domUtils'

import { isPaymentProviderPaymentAgentType } from '@model/PaymentProviderType'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { useFundingAmountStepper } from '@hooks/useFundingAmountStepper'

import { Box } from '@shared/components/Box'
import { CURRENCY_TYPE } from '@shared/components/CurrencyAmountChip/CurrencyAmountChip'
import { FundingCurrencySelect } from '@shared/components/Funding/FundingCurrencySelect'
import { FundingCurrencyTitle } from '@shared/components/Funding/FundingCurrencyTitle'
import { FundingFormWarningNote } from '@shared/components/Funding/FundingFormWarningNote'
import { FundingSummary } from '@shared/components/Funding/FundingSummary/FundingSummary'
import { Notice } from '@shared/components/Notice'
import { FullScreenFooter } from '@shared/templates/FullScreenLayout'
import { FundingTemplate } from '@shared/templates/FundingTemplate'

import { useWithdrawalTracker } from '@analytics/amplitude/withdrawal'

import type { WithdrawalFormValues } from '../WithdrawalFlowLayout/types'
import { MAX_WITHDRAWAL_AMOUNT, WithdrawalAmountField } from './WithdrawalAmountField'
import { WithdrawalFormFields } from './WithdrawalFormFields'
import {
  WithdrawalPaymentTermsConsent,
  WithdrawalTermsConditions,
} from './WithdrawalTermsConditions'
import { WithdrawalTips } from './WithdrawalTips'
import { useConfirmWithdrawal } from './hooks/useConfirmWithdrawal'

export const WithdrawalAmountPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const contentRef = useRef<HTMLDivElement>(null)

  const { handleConfirm, submitError, isSubmitting } = useConfirmWithdrawal()
  const tracker = useWithdrawalTracker()
  const apiClient = useApiClient(ClientApiClient)
  const { isDefaultCFDProductType } = useProductReadContext()
  const isCFD = isDefaultCFDProductType()
  const isMobile = useWindowResize()

  const { values, setFieldValue, validateForm, isValid } = useFormikContext<WithdrawalFormValues>()

  const provider = values.paymentProvider
  const categoryId = provider?.providerCategory?.id
  const isPaymentAgent = isPaymentProviderPaymentAgentType(categoryId)

  const {
    paymentAgent,
    isLoading: isAgentLoading,
    forceRefresh: forceRefreshAgent,
  } = useAgentInformationFetch(values.wallet?.id, isPaymentAgent ? provider?.id ?? '' : '')

  const poaAccepted = paymentAgent?.powerOfAttorney.accepted ?? false

  useEffect(() => {
    validateForm()
  }, [validateForm])

  const providerCurrencyId = provider?.currency?.id ?? values.wallet?.currency?.id ?? ''
  const summaryCurrency = providerCurrencyId || CURRENCY_TYPE.USD
  const { formatMoney } = useFormatNumber()

  const walletBalance = values.wallet.balance
  const hasInsufficientFunds = walletBalance <= 0

  const currencySelector = useFundingCurrencySelector({
    paymentProvider: provider,
    fields: values.fields,
    setFieldValue,
  })
  const isFundingCurrencySelected =
    !currencySelector.isVisible || Boolean(currencySelector.selectedCurrencyId)

  const onAmountBlur = () => {
    if (values.amount) {
      // tracker.amountEntered({
      //   amount: values.amount,
      //   currency: providerCurrencyId,
      //   amount_source: 'manual',
      // })
    }
  }

  const { canDecrease, canIncrease, onDecrease, onIncrease } = useFundingAmountStepper({
    amount: values.amount,
    maxValue: Math.min(MAX_WITHDRAWAL_AMOUNT, walletBalance),
    setAmount: (n) => setFieldValue('amount', n),
  })

  const handleAcceptAgentTerms = async () => {
    if (provider?.id) {
      await apiClient.paymentAgentAcceptTerms(provider.id)
      forceRefreshAgent()
    }
  }

  const onContinue = async () => {
    if (!isFundingCurrencySelected) return

    const errors = await validateForm()
    if (Object.keys(errors).length > 0) return
    await handleConfirm()
  }

  const showAmountSection = !isPaymentAgent || poaAccepted

  return (
    <FundingTemplate
      contentRef={contentRef}
      isLoading={isAgentLoading}
      onBack={() => navigate(-1)}
      maxWidth={456}
    >
      <Box
        flex
        direction='column'
        gap='x32'
        fullWidth
        align='center'
        pb={isMobile ? undefined : 'x32'}
        minHeight='100%'
      >
        {submitError && <Notice id='withdrawal-submit-error' type='danger' message={submitError} />}
        {hasInsufficientFunds && (
          <Notice
            id='withdrawal-insufficient-funds'
            type='danger'
            message={t('Wallet.Insufficient Funds')}
          />
        )}

        {currencySelector.isVisible ? (
          <Heading6 medium>{t('Withdrawal')}</Heading6>
        ) : (
          <FundingCurrencyTitle value={summaryCurrency} label={t('Withdrawal')} />
        )}

        {showAmountSection && (
          <Box flex direction='column' gap='x8' fullWidth align='center'>
            <Box flex align='start' gap='x16' fullWidth>
              <Box pt='x8'>
                <IconButton.Ghost
                  icon={{ icon: MinusIcon, color: 'text-low-em' }}
                  size='md'
                  disabled={!canDecrease}
                  onClick={onDecrease}
                />
              </Box>
              <WithdrawalAmountField onBlur={onAmountBlur} />
              <Box pt='x8'>
                <IconButton.Ghost
                  icon={{ icon: PlusIcon, color: 'text-low-em' }}
                  size='md'
                  disabled={!canIncrease}
                  onClick={onIncrease}
                />
              </Box>
            </Box>
            <Caption1 color='text-low-em'>
              {t('Wallet.Balance')} {formatMoney(walletBalance, summaryCurrency)}
            </Caption1>
          </Box>
        )}

        {showAmountSection && currencySelector.isVisible && (
          <FundingCurrencySelect
            options={currencySelector.options}
            value={currencySelector.selectedCurrencyId}
            fieldName={currencySelector.fieldName}
            label={currencySelector.label}
            required={currencySelector.required}
          />
        )}

        {showAmountSection && <FundingSummary paymentProvider={provider} wallet={values.wallet} />}

        {showAmountSection && <WithdrawalFormFields />}

        {isPaymentAgent && paymentAgent && (
          <Box fullWidth>
            <AgentInformation type={AgentInformationType.Withdraw} paymentAgent={paymentAgent} />
          </Box>
        )}

        <WithdrawalTermsConditions
          paymentAgent={paymentAgent}
          onPowerOfAttorneyAccepted={tracker.powerOfAttorneyAccepted}
          showPaymentTermsConsent={!isMobile}
        />

        <FullScreenFooter contentRef={contentRef}>
          {isMobile && <WithdrawalPaymentTermsConsent />}
          {isPaymentAgent && !poaAccepted ? (
            <Button.Primary
              size='lg'
              fullWidth
              disabled={!values.terms.powerOfAttorney}
              onClick={handleAcceptAgentTerms}
            >
              {t('Accept Agent')}
            </Button.Primary>
          ) : (
            <Button.Primary
              size='lg'
              fullWidth
              disabled={hasInsufficientFunds || !isValid}
              loading={isSubmitting}
              onClick={onContinue}
            >
              {t('Confirm')}
            </Button.Primary>
          )}
        </FullScreenFooter>

        {isCFD && <WithdrawalTips />}

        <FundingFormWarningNote paymentProvider={provider} />
      </Box>
    </FundingTemplate>
  )
}
